// @flow

import * as React from "react";
import axios from "axios";
import {toast} from "react-toastify";
import PlanCard from "./PlanCard.react";
import ScaleToFit from "./ScaleToFit.react";
import {startPlanCheckout, checkoutErrorMessage, showGenericErrorToast, isPaymentDeclined, openPaymentMethodUpdate} from "../Utils";
import {PLAN_CONFIRM_TIMEOUT_MS} from "../Constants";

const XHR_HEADERS = {headers: {"X-Requested-With": "XMLHttpRequest"}};

type Props = {|
  +userSettings: Object,
  +title: string,
  +message: string,
  // Only plans with more QR invoices a month than the company's current plan (it ran out); otherwise every paid plan.
  +upgradeOnly?: boolean,
  +onClose: () => void,
  // The company is now on plan (Paddle's webhook has landed): the caller carries on with what the person wanted.
  +onUpgraded: (plan: Object) => void,
|};

type State = {|
  billingType: number,
  plans: {[number]: Array<Object>},
  plansLoaded: number,     // how many of the two plan lists (monthly, yearly) have arrived
  checkoutPlan: ?Object,   // the plan whose checkout is open (company not paying yet)
  confirmingPlan: ?Object, // paid or switched; waiting for Paddle's webhook to move the company to it
  paymentDeclined: boolean,
|};

// Something only a (bigger) paid plan gives - emailing QR invoices on the Free plan, more QR invoices once the monthly
// allowance has run out - asked for by the company: says so and shows the plans, with the same cards as the legacy-plan
// modal. Unlike that modal it can be closed straight away (X, Escape or a click outside): nobody is forced to choose.
// How a plan is bought depends on whether the company is already paying:
//  - not paying (Free, or a cancelled subscription running out): a new Paddle checkout (startPlanCheckout, as on the
//    Usage & Plan page);
//  - paying: the EXISTING subscription is switched in place (/subscriptions/change_plan, prorated and billed now, the
//    same call as the Usage & Plan page and LegacyPlanModal). Never a second checkout: that would charge twice.
// Only an admin can choose; members see the plans with a note. Either way it waits until Paddle's webhook has moved the
// company to the plan (/check_upgrade_status), then calls onUpgraded.
class PremiumFeatureModal extends React.Component<Props, State> {
    state = {
        // A paying company opens on its own billing period; everyone else on the yearly plans, like the legacy modal.
        billingType: this.props.userSettings.has_active_paid_subscription && this.props.userSettings.plan_billing_type === 1 ? 1 : 2,
        plans: {1: [], 2: []},
        plansLoaded: 0,
        checkoutPlan: null,
        confirmingPlan: null,
        paymentDeclined: false,
    };

    confirmInterval: ?IntervalID = null;
    confirmTimeout: ?TimeoutID = null;
    unmounted: boolean = false;
    switchRequested: boolean = false;  // set synchronously: a fast double-click must not send two plan changes to Paddle

    componentDidMount() {
        [1, 2].forEach((billingType) => {
            axios.get(window.SERVER_URL + "/get_all_plans?billing_type=" + billingType).then((res) => {
                if (!this.unmounted) {
                    this.setState((prev) => ({plans: {...prev.plans, [billingType]: this.offeredPlans(res.data.result || [])},
                                              plansLoaded: prev.plansLoaded + 1}));
                }
            }).catch(() => toast.error("We couldn't load the plans. Please try again."));
        });
        document.addEventListener("keydown", this.handleKeyDown);
    }

    componentWillUnmount() {
        this.unmounted = true;
        this.stopConfirming();
        document.removeEventListener("keydown", this.handleKeyDown);
    }

    // The paid plans to offer: all of them, or (upgradeOnly) only those with more QR invoices a month than the current plan.
    offeredPlans(plans: Array<Object>): Array<Object> {
        const settings = this.props.userSettings;
        const currentQuota = Number(settings.plan_max_qr_invoices_per_month) || 0;
        return plans.filter((plan) => plan.id !== 1 && plan.id !== settings.plan_id
            && (!this.props.upgradeOnly || Number(plan.max_qr_invoices) > currentQuota));
    }

    handleKeyDown = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
            this.close();
        }
    };

    // Not while a payment is being confirmed: closing then would leave the person unsure whether it went through.
    close = () => {
        if (this.state.confirmingPlan === null) {
            this.props.onClose();
        }
    };

    stopConfirming() {
        if (this.confirmInterval) {
            clearInterval(this.confirmInterval);
        }
        if (this.confirmTimeout) {
            clearTimeout(this.confirmTimeout);
        }
    }

    choosePlan = (plan: Object) => {
        if (this.state.checkoutPlan || this.state.confirmingPlan || this.switchRequested) {
            return;
        }
        const settings = this.props.userSettings;
        if (settings.has_active_paid_subscription) {
            if (settings.is_active === false) {
                // Frozen after a failed payment: a plan change would charge the card that just failed.
                toast.error("Your last payment failed, so your subscription is frozen. Please update your payment method on the Usage & Plan page first.");
                return;
            }
            this.switchPlan(plan);
            return;
        }
        this.setState({checkoutPlan: plan});
        startPlanCheckout(plan.id, settings, (data) => this.waitForPlan(Number(data.product.id)),
                          () => this.setState({checkoutPlan: null}))
            .catch((err) => {
                this.setState({checkoutPlan: null});
                toast.error(checkoutErrorMessage(err));
            });
    };

    // A paying company: its existing Paddle subscription moves to the plan (prorated, billed now).
    switchPlan(plan: Object) {
        this.switchRequested = true;
        this.setState({confirmingPlan: plan, paymentDeclined: false});
        axios.post(window.SERVER_URL + "/subscriptions/change_plan", {new_plan_id: plan.id}, XHR_HEADERS).then(() => {
            this.waitForPlan(plan.id);
        }).catch((err) => {
            this.switchRequested = false;
            this.setState({confirmingPlan: null});
            if (isPaymentDeclined(err)) {
                // The bank refused the prorated charge: nothing changed. Say so and open Paddle's card-update page.
                this.setState({paymentDeclined: true});
                this.updatePaymentMethod();
            } else if (err.response && err.response.status === 403) {
                toast.error("Only a company admin can change the plan.");
            } else {
                showGenericErrorToast();
            }
        });
    }

    updatePaymentMethod = () => {
        const opened = openPaymentMethodUpdate(this.props.userSettings.update_url, () => {
            this.setState({paymentDeclined: false});
            toast.success("Payment method updated. Please choose your plan again.");
        });
        if (!opened) {
            toast.error("We couldn't open the payment update page. Please contact support.");
        }
    };

    openSupportChat = () => {
        window.$crisp = window.$crisp || [];
        window.$crisp.push(["do", "chat:show"]);
        window.$crisp.push(["do", "chat:open"]);
    };

    // Paddle took the payment (or accepted the switch); the company moves to the plan when its webhook lands.
    waitForPlan(planId: number) {
        const plan = [...this.state.plans[1], ...this.state.plans[2]].find((p) => p.id === planId) || {id: planId};
        this.setState({checkoutPlan: null, confirmingPlan: plan});
        this.confirmInterval = setInterval(() => {
            axios.post(window.SERVER_URL + "/check_upgrade_status", {newPlanId: plan.id}).then((res) => {
                if (res.data.upgrade_status === true) {
                    this.stopConfirming();
                    this.props.onUpgraded(plan);
                }
            }).catch(() => {});
        }, 1000);
        this.confirmTimeout = setTimeout(() => {
            this.stopConfirming();
            this.switchRequested = false;
            this.setState({confirmingPlan: null});
            toast.error("Your payment went through, but confirming it is taking longer than expected. Please refresh this page in a minute - if the plan still hasn't updated, contact support.");
        }, PLAN_CONFIRM_TIMEOUT_MS);
    }

    renderPaymentDeclined(): React.Node {
        return (
            <div className="legacy-plan-declined" role="alert">
                <div>
                    <b>Your bank declined the charge.</b> We couldn't charge your card for this plan, so nothing was changed.
                    Please update your payment method, then choose your plan again.
                </div>
                <div className="legacy-plan-declined-actions">
                    <button type="button" className="btn btn-primary btn-sm" onClick={this.updatePaymentMethod}>Update payment method</button>
                    <button type="button" className="btn btn-outline btn-sm" onClick={this.openSupportChat}>Contact support</button>
                </div>
            </div>
        );
    }

    render(): React.Node {
        const isAdmin = this.props.userSettings.is_admin === true;
        const plans = this.state.plans[this.state.billingType];
        const busy = this.state.checkoutPlan !== null || this.state.confirmingPlan !== null;
        // Already on the biggest plan (nothing to upgrade to, in either billing period)
        const noBiggerPlan = this.props.upgradeOnly && this.state.plansLoaded === 2
            && this.state.plans[1].length === 0 && this.state.plans[2].length === 0;
        return (
            <div className="app-modal-overlay legacy-plan-overlay" onClick={this.close}>
              <ScaleToFit>
                <div className="app-modal-dialog legacy-plan-modal" role="dialog" aria-modal="true" aria-labelledby="premium-feature-title"
                     onClick={(e) => e.stopPropagation()}>
                    <button type="button" className="app-modal-close premium-feature-close" aria-label="Close"
                            disabled={this.state.confirmingPlan !== null} onClick={this.close}>&times;</button>
                    <div className="app-modal-body">
                        <div className="legacy-plan-header">
                            <h2 id="premium-feature-title" className="premium-feature-title">{this.props.title}</h2>
                            {noBiggerPlan ? (
                                <p>
                                    You're already on our biggest plan.{" "}
                                    <button type="button" className="premium-feature-link" onClick={this.openSupportChat}>Contact support</button>{" "}
                                    and we'll help you raise your limit.
                                </p>
                            ) : <p>{this.props.message}</p>}
                            {isAdmin || noBiggerPlan ? null : (
                                <p className="premium-feature-admin-note">Only a company admin can choose a plan. Please ask your admin to upgrade.</p>
                            )}
                        </div>
                        {noBiggerPlan ? null : (
                            <React.Fragment>
                                <div className="billing-type-toggle" style={{marginBottom: "20px"}}>
                                    <div style={{display: "inline-flex", border: "1px solid #d5dbe3", borderRadius: "8px", overflow: "hidden"}}>
                                        <button className={"btn btn-sm" + (this.state.billingType === 1 ? " btn-primary" : " btn-outline")}
                                                style={{borderRadius: 0}} disabled={busy}
                                                onClick={() => this.setState({billingType: 1})}>Monthly</button>
                                        <button className={"btn btn-sm" + (this.state.billingType === 2 ? " btn-primary" : " btn-outline")}
                                                style={{borderRadius: 0}} disabled={busy}
                                                onClick={() => this.setState({billingType: 2})}>Yearly (Get 2 months free <span role="img" aria-label="celebration">🎉</span>)</button>
                                    </div>
                                </div>
                                {this.state.paymentDeclined ? this.renderPaymentDeclined() : null}
                                <div className="app-grid-row">
                                    {plans.map((plan) => {
                                        const confirming = this.state.confirmingPlan && this.state.confirmingPlan.id === plan.id;
                                        return (
                                            <div className="app-grid-col plan-card-col" key={plan.id} style={{flex: "1 1 200px"}}>
                                                <PlanCard plan={plan} button={
                                                    <button className="btn btn-primary" disabled={!isAdmin || busy} onClick={() => this.choosePlan(plan)}>
                                                        {confirming ? (
                                                            <span style={{display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "8px"}}>
                                                                <span className="btn-spinner" />
                                                                Activating…
                                                            </span>
                                                        ) : "Choose This Plan"}
                                                    </button>
                                                } />
                                            </div>
                                        );
                                    })}
                                </div>
                            </React.Fragment>
                        )}
                    </div>
                </div>
              </ScaleToFit>
            </div>
        );
    }
}

export default PremiumFeatureModal;
